import { useEffect, useRef } from 'react';
import type { PwaPlatform } from './pwa-install';

/**
 * Règles de déclenchement & rythme de la popin d'installation (spec 12 §5) — pures
 * et testables, isolées du rendu. Tout est gouverné par un flag de build OFF par
 * défaut (`VITE_PWA_INSTALL_PROMPT`) : tant qu'il n'est pas `on`, RIEN ne se déclenche
 * et l'entrée permanente reste masquée (la prod n'est pas impactée). Le flag n'est
 * activé qu'une fois les captures iOS produites et la vérif appareil faite (HUMAN_TODO).
 */

const STORAGE_KEY = 'rp:pwa-install';
const SNOOZE_DAYS = 30;
/** Affichages AUTO max : 1 initial + 2 relances (spec 12 §5). Au-delà : entrée permanente seule. */
const MAX_AUTO_PROMPTS = 3;
const DAY_MS = 24 * 60 * 60 * 1000;

export interface InstallState {
  platform: PwaPlatform;
  isInstalled: boolean;
  canPrompt: boolean;
}

interface InstallPromptStore {
  /** Jour calendaire (YYYY-MM-DD, local) du tout premier chargement. */
  firstVisitDay?: string;
  /** L'utilisateur a terminé au moins une partie. */
  gamePlayed?: boolean;
  /** Nombre de refus de la popin AUTO (plafonné par `MAX_AUTO_PROMPTS`). */
  dismissCount?: number;
  /** Horodatage (ms) du dernier refus — fenêtre de re-proposition de 30 j. */
  dismissedAt?: number;
}

/** Le flag est lu paresseusement (les tests le stubbent via `vi.stubEnv`). */
export function isInstallPromptEnabled(): boolean {
  return import.meta.env.VITE_PWA_INSTALL_PROMPT === 'on';
}

function dayKey(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function read(): InstallPromptStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as InstallPromptStore) : {};
  } catch {
    return {};
  }
}

function write(patch: Partial<InstallPromptStore>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...read(), ...patch }));
  } catch {
    // Stockage indisponible (mode privé, quota) : on ignore silencieusement.
  }
}

/** Mémorise le jour de la première visite (déclencheur « 2ᵉ visite »). */
export function recordVisit(now: Date = new Date()): void {
  if (!isInstallPromptEnabled()) return;
  if (!read().firstVisitDay) write({ firstVisitDay: dayKey(now) });
}

/** Mémorise qu'une partie a été jouée (déclencheur « première partie »). */
export function recordGamePlayed(): void {
  if (!isInstallPromptEnabled()) return;
  if (!read().gamePlayed) write({ gamePlayed: true });
}

/** Enregistre un refus de la popin AUTO (compteur + fenêtre de snooze). */
export function recordDismissed(now: Date = new Date()): void {
  if (!isInstallPromptEnabled()) return;
  write({ dismissCount: (read().dismissCount ?? 0) + 1, dismissedAt: now.getTime() });
}

/** Engagement atteint : partie jouée OU 2ᵉ visite un jour calendaire distinct. */
export function hasEngaged(now: Date = new Date()): boolean {
  const store = read();
  if (store.gamePlayed) return true;
  return store.firstVisitDay != null && store.firstVisitDay !== dayKey(now);
}

/** Snooze actif : plafond de relances atteint, ou moins de 30 j depuis le dernier refus. */
export function isSnoozeActive(now: Date = new Date()): boolean {
  const store = read();
  if ((store.dismissCount ?? 0) >= MAX_AUTO_PROMPTS) return true;
  if (store.dismissedAt == null) return false;
  return now.getTime() - store.dismissedAt < SNOOZE_DAYS * DAY_MS;
}

/**
 * La popin peut-elle s'afficher AUTOMATIQUEMENT ? (flag on, pas installé, plateforme
 * capable — Chromium exige un `beforeinstallprompt` capté —, engagement atteint, hors
 * snooze). Jamais au premier chargement : l'engagement l'interdit.
 */
export function shouldAutoShow(state: InstallState, now: Date = new Date()): boolean {
  if (!isInstallPromptEnabled()) return false;
  if (state.isInstalled) return false;
  if (state.platform === 'other') return false;
  if (state.platform === 'chromium' && !state.canPrompt) return false;
  if (!hasEngaged(now)) return false;
  return !isSnoozeActive(now);
}

/**
 * Entrée permanente (« Installer l'app » dans le menu) disponible ? Sans condition
 * d'engagement ni de snooze — mais seulement si l'action peut aboutir (iOS = tutoriel
 * toujours possible ; Chromium = besoin d'un prompt capté).
 */
export function isPermanentEntryAvailable(state: InstallState): boolean {
  if (!isInstallPromptEnabled()) return false;
  if (state.isInstalled) return false;
  if (state.platform === 'ios') return true;
  return state.platform === 'chromium' && state.canPrompt;
}

/** Enregistre « partie jouée » une seule fois, quand `played` passe à true. */
export function useMarkGamePlayed(played: boolean): void {
  const marked = useRef(false);
  useEffect(() => {
    if (played && !marked.current) {
      marked.current = true;
      recordGamePlayed();
    }
  }, [played]);
}
